import client from "services/ngage/api-client";
import {Link, LinkAPIData} from "types/Link";
import deserializeLink from "services/ngage/deserializers/link";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";

export type LinkFilters = {
  linkableType?: string,
  linkableId?: number,
  category?: string,
  attached?: boolean
  featured?: boolean
  published?: boolean
  sort?: string
  page?: number
  perPage?: number
}

/**
 * Get the number of links that have been created
 *
 * @param filters
 */
const count = async (filters: LinkFilters = {}) => {
  const params = Object.assign({page: 1, per_page: 1}, snakeKeys(filters))

  try {
    const page = (await client.get<DataResource<LinkFilters> | null>('/links', {params})).data

    return page?.meta.total ?? 0
  } catch (error) {
    console.error("Unable to fetch links", error)
    return 0
  }
}

/**
 * Get all links (paginated)
 */
const filtered = async (filters: LinkFilters = {}) => {
  const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

  try {
    const page = (await client.get<DataResource<Link> | null>('/links', {params})).data

    if (page) page.data = page.data.map(deserializeLink)

    return page
  } catch (error) {
    console.error("Unable to fetch links", error)
    return null
  }
}

/**
 * Get a single link
 *
 * @param {number} linkId
 */
const get = async (linkId: number) => {
  try {
    const link = (await client.get<Link | null>(`/links/${linkId}`)).data
    return link ? deserializeLink(link) : null
  } catch (error) {
    console.error(`Unable to fetch link ${linkId}`, error)
    return null
  }
}

/**
 * Store a new link
 *
 * @param data
 */
const store = async (data: LinkAPIData) => {
  try {
    const link = (await client.post<Link | null>('/links', data)).data
    return link ? deserializeLink(link) : null
  } catch (error) {
    console.error("Unable to store link", error)
    throw error
  }
}

/**
 * Update an existing link
 *
 * @param {number} linkId
 * @param data
 */
const update = async (linkId: number, data: LinkAPIData) => {
  try {
    const link = (await client.put<Link | null>(`/links/${linkId}`, data)).data
    return link ? deserializeLink(link) : null
  } catch (error) {
    console.error(`Unable to update link ${linkId}`, error)
    throw error
  }
}

/**
 * Permanently delete a link
 *
 * @param {number} linkId
 */
const destroy = async (linkId: number) => {
  try {
    return (await client.delete<null>(`/links/${linkId}`)).status === 204
  } catch (error) {
    console.error(`Unable to delete link ${linkId}`, error)
    throw error
  }
}

const links = {
  count,
  filtered,
  get,
  store,
  update,
  destroy
}

export default links;